import React, { useState, useEffect } from 'react'
import {
  Receipt,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Search
} from 'lucide-react'
import api from '../services/api'
import Loader from '../components/Loader'
import TransactionsSkeleton from '../components/skeletons/TransactionsSkeleton'

import { GeometricBackground } from '../components/layouts/GeometricBackground'

function Transactions() {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadTransactions()
  }, [])

  const loadTransactions = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await api.get('/transactions')
      setTransactions(response.data.transactions || [])
    } catch (err) {
      console.error('❌ Failed to load transactions:', err)
      setError(err.userMessage || 'Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const updateTransactionStatus = async (transactionId, status) => {
    try {
      await api.put(`/transactions/${transactionId}/status`, { status })
      await loadTransactions()
    } catch (err) {
      console.error('❌ Failed to update transaction:', err)
      setError(err.response?.data?.message || err.userMessage || 'Failed to update transaction')
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-secondary-500" />
      case 'failed':
        return <XCircle className="h-5 w-5 text-danger-500" />
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-gray-500" />
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-secondary-600 bg-secondary-50 dark:bg-secondary-900/20'
      case 'failed':
        return 'text-danger-600 bg-danger-50 dark:bg-danger-900/20'
      case 'cancelled':
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20'
      default:
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
    }
  }

  const filteredTransactions = transactions.filter(transaction => {
    const matchesFilter = filter === 'all' || transaction.status === filter
    const matchesSearch = !searchTerm ||
      transaction.merchant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.note?.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesFilter && matchesSearch
  })

  const getTransactionStats = () => {
    const stats = {
      total: transactions.length,
      success: transactions.filter(t => t.status === 'success').length,
      pending: transactions.filter(t => t.status === 'pending').length,
      failed: transactions.filter(t => t.status === 'failed').length + transactions.filter(t => t.status === 'cancelled').length,
      totalAmount: transactions
        .filter(t => t.status === 'success')
        .reduce((sum, t) => sum + t.amount, 0)
    }
    return stats
  }

  if (loading && transactions.length === 0) {
    return <TransactionsSkeleton />
  }

  const stats = getTransactionStats()

  return (
    <div className="relative min-h-screen bg-[#030303]">
      <GeometricBackground variant="default" className="fixed inset-0" />
      <div className="relative z-10 p-4 sm:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Transactions
            </h1>
            <p className="text-white/60">
              View and manage your payment history
            </p>
          </div>

          <button
            onClick={loadTransactions}
            className="btn-outline flex items-center space-x-2"
            disabled={loading}
          >
            {loading ? <Loader size="sm" className="mr-2" /> : <RefreshCw className="h-4 w-4" />}
            <span>Refresh</span>
          </button>
        </div>

        {error && (
          <div className="mb-6 flex items-center space-x-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Stats */}
        {transactions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6 mb-8">
            <div className="glass-card p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Total Transactions</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.total}
                  </p>
                </div>
                <div className="p-3 bg-primary-500/20 rounded-xl">
                  <Receipt className="h-6 w-6 text-primary-400" />
                </div>
              </div>
            </div>

            <div className="glass-card p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Successful</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {stats.success}
                  </p>
                </div>
                <div className="p-3 bg-emerald-500/20 rounded-xl">
                  <CheckCircle className="h-6 w-6 text-emerald-400" />
                </div>
              </div>
            </div>

            <div className="glass-card p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Pending</p>
                  <p className="text-2xl font-bold text-amber-400">
                    {stats.pending}
                  </p>
                </div>
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <Clock className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </div>

            <div className="glass-card p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Total Spent</p>
                  <p className="text-2xl font-bold text-white">
                    ₹{stats.totalAmount.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        {transactions.length > 0 && (
          <div className="glass-card p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-white/60" />
                  <span className="text-sm font-medium text-white/80">Filter:</span>
                </div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="bg-white/5 border border-white/10 text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-2.5 [&>option]:bg-gray-900"
                >
                  <option value="all">All Status</option>
                  <option value="success">Successful</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-white/5 border border-white/10 text-white text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 p-2.5 placeholder-white/30"
                />
              </div>
            </div>
          </div>
        )}

        {/* Transactions List */}
        {filteredTransactions.length > 0 ? (
          <div className="glass-card p-4 sm:p-6">
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="p-4 border border-white/5 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 overflow-hidden">
                      <div className="flex-shrink-0 p-3 bg-white/5 rounded-full">
                        <Receipt className="h-5 w-5 text-white/60" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-medium text-white truncate">
                            {transaction.merchant_name || 'Unknown Merchant'}
                          </h3>
                          {getStatusIcon(transaction.status)}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/50">
                          <span className="truncate max-w-[150px]">{transaction.category_name}</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="whitespace-nowrap">{new Date(transaction.created_at).toLocaleDateString()}</span>
                          <span className="text-xs sm:text-sm">{new Date(transaction.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>

                        {transaction.merchant_upi && (
                          <p className="text-xs text-white/40 mt-1 truncate">
                            {transaction.merchant_upi}
                          </p>
                        )}

                        {transaction.note && (
                          <p className="text-xs text-white/50 mt-1 italic truncate">
                            "{transaction.note}"
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center pl-14 sm:pl-0">
                      <p className="font-semibold text-white sm:mb-1">
                        -₹{transaction.amount.toLocaleString('en-IN')}
                      </p>

                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>

                        {transaction.status === 'pending' && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => updateTransactionStatus(transaction.id, 'success')}
                              className="p-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
                              title="Mark as successful"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => updateTransactionStatus(transaction.id, 'failed')}
                              className="p-1 bg-rose-500/20 text-rose-400 rounded hover:bg-rose-500/30"
                              title="Mark as failed"
                            >
                              <AlertCircle className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : transactions.length > 0 ? (
          <div className="glass-card text-center py-12">
            <Search className="h-12 w-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              No transactions found
            </h3>
            <p className="text-white/60">
              Try adjusting your filters or search terms
            </p>
          </div>
        ) : (
          <div className="glass-card text-center py-12">
            <Receipt className="h-12 w-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              No transactions yet
            </h3>
            <p className="text-white/60">
              Your payment history will appear here once you make transactions
            </p>
          </div>
        )
        }
      </div>
    </div>
  )
}

export default Transactions